import { AccountBalance, ChevronRight, Groups, OpenInNew } from "@mui/icons-material";
import { Button, Chip, IconButton, Tooltip } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import DisplayDate from "components/DisplayDate";
import { RefreshData } from "components/helper";
import { P } from "const/permissions";
import useKothar from "context/useKothar";
import { usePermission } from "hooks/usePermissions";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { FaPlusCircle } from "react-icons/fa";
import { MdInsertChartOutlined } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import { getMrtBodyRowSx, getMrtTableThemeProps } from "theme/mrtTableTheme";
import AdmissionFiltersPanel, { EMPTY_ADMISSION_FILTERS } from "./AdmissionFiltersPanel";
import { fetchAdmissionClientsPage, fetchAdmissionIntakes } from "./admissionListApi";

const Students = () => {
  const [{ uniData }, { refetchUniData }] = useKothar();
  const navigate = useNavigate();
  const canViewAdmissionSummary = usePermission(P.ADMISSIONS.READ);
  const [filters, setFilters] = useState(EMPTY_ADMISSION_FILTERS);

  useEffect(() => {
    if (!uniData?.length) refetchUniData();
  }, []);

  // Reset to first page whenever a filter (not pagination) changes.
  useEffect(() => {
    setFilters((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }));
  }, [
    filters.caseOfficer,
    filters.course,
    filters.university,
    filters.intake,
    filters.status,
    filters.accountVerificationStatus,
  ]);

  const clientsQuery = useQuery(
    ["admission-clients-page", filters],
    () =>
      fetchAdmissionClientsPage({
        page: filters.pageIndex,
        pageSize: filters.pageSize,
        search: filters.search,
        filters,
      }),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  const intakesQuery = useQuery(["student-intakes"], fetchAdmissionIntakes, {
    refetchOnWindowFocus: false,
  });
  const intakeOptions = (intakesQuery.data ?? []).map((i) => ({ label: i, value: i }));

  const pageData = clientsQuery.data ?? { content: [], totalItems: 0 };
  const clients = pageData.content ?? [];
  const totalItems = pageData.totalItems ?? 0;
  const listLoading = clientsQuery.isFetching;

  const openClient = (client) => {
    navigate(`/admin/admission/client/${client.clientId}`);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Client",
        size: 240,
        Cell: ({ row }) => {
          const client = row.original;
          return (
            <div className="min-w-0">
              <div className="font-medium text-app-text truncate">
                {client.passportNumber
                  ? `${client.name} · ${client.passportNumber}`
                  : client.name || "—"}
              </div>
              {client.reference && (
                <div className="text-xs text-app-text-muted truncate">Ref: {client.reference}</div>
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 200,
        Cell: ({ cell }) => <span className="text-sm">{cell.getValue() || "—"}</span>,
      },
      {
        accessorKey: "number",
        header: "Phone",
        size: 140,
        Cell: ({ cell }) => <span className="text-sm">{cell.getValue() || "—"}</span>,
      },
      {
        accessorKey: "admissionCount",
        header: "Admissions",
        size: 110,
        Cell: ({ cell }) => (
          <Chip size="small" color="primary" variant="outlined" label={cell.getValue() ?? 0} />
        ),
      },
      {
        accessorKey: "caseOfficer",
        header: "Case Officer",
        size: 150,
        Cell: ({ cell }) => <span className="text-sm">{cell.getValue() || "—"}</span>,
      },
      {
        accessorKey: "latestActivityDate",
        header: "Last activity",
        size: 140,
        Cell: ({ cell }) => <DisplayDate value={cell.getValue()} className="text-sm" />,
      },
      {
        id: "actions",
        header: "Actions",
        size: 90,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="flex items-center justify-end">
            <Tooltip title="Open client admissions">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openClient(row.original);
                }}
              >
                <ChevronRight />
              </IconButton>
            </Tooltip>
            <Tooltip title="Client profile">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/admin/client/view/${row.original.clientId}`);
                }}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Direct account">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate("/admin/account/add", {
                    state: {
                      item: {
                        clientData: {
                          id: row.original.clientId,
                          name: row.original.name,
                          email: row.original.email,
                          number: row.original.number,
                          caseOfficer: row.original.caseOfficer,
                        },
                      },
                    },
                  });
                }}
              >
                <AccountBalance fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigate]
  );

  const table = useMaterialReactTable({
    columns,
    data: clients,
    getRowId: (row) => String(row.clientId),
    enableRowNumbers: true,
    rowNumberDisplayMode: "original",
    enableGlobalFilter: true,
    enableColumnFilters: false,
    enableStickyHeader: true,
    enableColumnPinning: true,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalItems,
    pageCount: Math.ceil(totalItems / filters.pageSize) || 0,
    onPaginationChange: setFilters,
    onGlobalFilterChange: (value) => {
      setFilters((prev) => ({ ...prev, search: value, pageIndex: 0 }));
    },
    initialState: {
      density: "compact",
      showGlobalFilter: true,
      columnPinning: {
        left: ["mrt-row-numbers", "name"],
        right: ["actions"],
      },
    },
    muiSearchTextFieldProps: {
      placeholder: "Search name, email, phone, college…",
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 50, 100],
    },
    paginationDisplayMode: "pages",
    state: {
      isLoading: listLoading,
      showProgressBars: listLoading,
      globalFilter: filters.search || "",
      pagination: { pageIndex: filters.pageIndex, pageSize: filters.pageSize },
    },
    ...getMrtTableThemeProps({ maxHeight: "calc(100vh - 320px)" }),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isActionClick =
          event.target.closest(".MuiIconButton-root") || event.target.closest("button");
        if (!isActionClick) openClient(row.original);
      },
      sx: getMrtBodyRowSx(row),
    }),
    renderTopToolbarCustomActions: () => (
      <>
        <AdmissionFiltersPanel
          embedded
          mode="clients"
          filters={filters}
          setFilters={setFilters}
          uniData={uniData}
          intakeOptions={intakeOptions}
        />
      </>
    ),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h3 className={"font-semibold text-lg flex items-center gap-2 " + "text-app-text"}>
                  <Groups fontSize="small" />
                  Admissions · Clients ({totalItems})
                  <RefreshData refreshData={clientsQuery.refetch} loading={listLoading} />
                </h3>
                <p className="text-sm text-app-text-muted mt-0.5">
                  Open a client to see all their admissions.
                </p>
              </div>
              <div className="flex items-center gap-3">
                {canViewAdmissionSummary && (
                  <Button
                    variant="outlined"
                    startIcon={<MdInsertChartOutlined />}
                    component={Link}
                    to="/admin/university/report"
                  >
                    Admission Summary
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={<FaPlusCircle />}
                  component={Link}
                  to="/admin/admission/add"
                >
                  Add Student
                </Button>
              </div>
            </div>
          </div>

          <div className="px-4 pb-4">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Students;
